/**
 * The Spyglasses categorical chart palette.
 *
 * Every value here is a CSS variable reference, so charts follow the theme and
 * respond to light/dark without any JS. Recharts accepts `var(--x)` directly in
 * `fill`/`stroke`, so these can be passed straight through.
 *
 * Rules (see docs/design-system.md):
 *  - Slots are assigned in fixed order and NEVER cycled. A 7th series folds into
 *    CHART_OTHER, gets faceted, or gets direct-labelled — it never reuses slot 1.
 *  - Colour follows the entity, not its rank. Filtering a series out must not
 *    repaint the survivors — resolve a colour from a stable key, not an index
 *    into the current array. Use `chartSeriesByKey` for that.
 *  - Slot colours are for marks only. Labels, values and legends wear text
 *    tokens (`text-foreground` / `text-muted-foreground`).
 *  - positive / negative / neutral are reserved for direction-of-change and are
 *    never used as a series colour.
 *
 * Validated with the `dataviz` skill's checker against both surfaces:
 *   light (#ffffff): adjacent CVD ΔE 23.9, normal 26.9 · all-pairs CVD 10.1, normal 15.3
 *   dark  (#070d12): adjacent CVD 17.7, normal 25.7 · all-pairs CVD 10.1, normal 15.8
 * In light mode `--chart-3` and `--chart-5` fall below 3:1 against white, so
 * charts that use slots 3 or 5 must ship direct labels or a table view.
 */

export const CHART_SERIES = [
	"var(--chart-1)",
	"var(--chart-2)",
	"var(--chart-3)",
	"var(--chart-4)",
	"var(--chart-5)",
	"var(--chart-6)",
] as const;

/** The "Other" / unattributed bucket. Not a categorical slot. */
export const CHART_OTHER = "var(--chart-other)";

/** Sequential ramp in the brand hue, low → high. */
export const CHART_RAMP = [
	"var(--chart-ramp-1)",
	"var(--chart-ramp-2)",
	"var(--chart-ramp-3)",
	"var(--chart-ramp-4)",
	"var(--chart-ramp-5)",
] as const;

export const MAX_CHART_SERIES = CHART_SERIES.length;

/**
 * The first `count` series colours. Anything past slot 6 becomes CHART_OTHER —
 * we never invent a 7th hue.
 */
export function chartSeries(count: number): string[] {
	return Array.from({ length: Math.max(0, count) }, (_, i) =>
		i < MAX_CHART_SERIES ? CHART_SERIES[i] : CHART_OTHER,
	);
}

/**
 * Stable colour assignment keyed by entity. Pass the full, unfiltered list of
 * keys in a stable order (alphabetical, or by all-time volume — anything that
 * doesn't move when a filter changes) and look colours up by key. This is what
 * keeps a series the same colour when its neighbours are filtered away.
 */
export function chartSeriesByKey(
	allKeys: readonly string[],
): Map<string, string> {
	const map = new Map<string, string>();
	allKeys.forEach((key, i) => {
		map.set(key, i < MAX_CHART_SERIES ? CHART_SERIES[i] : CHART_OTHER);
	});
	return map;
}

/**
 * `count` evenly-spaced steps of the sequential ramp, for heatmaps, density
 * cells and choropleths. Always a single hue, light → dark (light mode) or
 * deep → bright (dark mode); never a rainbow.
 */
export function chartRamp(count: number): string[] {
	if (count <= 1) {
		return [CHART_RAMP[CHART_RAMP.length - 1]];
	}
	if (count >= CHART_RAMP.length) {
		return [...CHART_RAMP];
	}
	return Array.from({ length: count }, (_, i) => {
		const pos = (i / (count - 1)) * (CHART_RAMP.length - 1);
		return CHART_RAMP[Math.round(pos)];
	});
}

/** Bucket a 0–1 intensity into a ramp step. */
export function rampStepFor(intensity: number): string {
	const clamped = Math.min(1, Math.max(0, intensity));
	const idx = Math.min(
		CHART_RAMP.length - 1,
		Math.floor(clamped * CHART_RAMP.length),
	);
	return CHART_RAMP[idx];
}

export type DeltaTone = "positive" | "negative" | "neutral";

export interface DeltaToneOptions {
	/**
	 * True when a *decrease* is the good outcome (hallucination rate, time to
	 * first citation, error counts). Flips which direction reads positive.
	 */
	lowerIsBetter?: boolean;
	/** Changes with magnitude below this read as "no meaningful change". */
	epsilon?: number;
}

/**
 * Map a signed change to a semantic tone. Direction is not universal — a metric
 * where down is good must pass `lowerIsBetter`, otherwise the badge will tell
 * the user the opposite of the truth.
 */
export function deltaTone(
	change: number | null | undefined,
	{ lowerIsBetter = false, epsilon = 0 }: DeltaToneOptions = {},
): DeltaTone {
	if (change == null || Number.isNaN(change) || Math.abs(change) <= epsilon) {
		return "neutral";
	}
	const isUp = change > 0;
	const isGood = lowerIsBetter ? !isUp : isUp;
	return isGood ? "positive" : "negative";
}

/** Tailwind classes for each tone — the canonical badge/pill treatment. */
export const DELTA_TONE_CLASSES: Record<DeltaTone, string> = {
	positive: "bg-positive-subtle text-positive border-positive-border",
	negative: "bg-negative-subtle text-negative border-negative-border",
	neutral: "bg-neutral-subtle text-neutral border-neutral-border",
};

/**
 * Resolve a token to a concrete colour string for contexts that can't evaluate
 * `var()` — canvas rendering, PNG/PDF export, emailed charts.
 *
 * Browser-only; returns `fallback` during SSR.
 */
export function resolveToken(token: string, fallback = "#000000"): string {
	if (typeof window === "undefined") {
		return fallback;
	}
	const name = token
		.trim()
		.replace(/^var\(\s*/, "")
		.replace(/\s*\)$/, "");
	const value = getComputedStyle(document.documentElement)
		.getPropertyValue(name)
		.trim();
	return value || fallback;
}

/** Resolve a whole palette at once, for export paths. */
export function resolvePalette(tokens: readonly string[]): string[] {
	return tokens.map((t) => resolveToken(t));
}
